import configJSON from 'config.json';
import { CountryCodeList } from 'types/countries';
import { tcpaConsent } from 'domain/consent';
import { SiteLanguage } from 'types/locales';
import ExprealtyUsTheme from './theme-config';
import { createHomeConfig } from './home-config';
import { createJoinExpConfig } from './join-exp-config';
import { createCurrencyMap } from '@tenants/utils/currency-map';
import {
  EXP_REALTY_US_TENANT_ID,
  EXP_REALTY_US_TENANT_ID_TYPE,
  ContentHosts,
  DEFAULT_GTM_CONTAINER_ID_KEY,
  AllowedExperimentKey,
} from '@tenants/types';
import { createFooterConfig } from './footer-config';
import { createHeaderConfig } from './header-config';
import { createOrganizationConfig } from './organization-config';
import { createHeadTagsConfig } from './head-tags-config';
import { createAddressPageConfig } from './address-page-config';
import { Namespaces } from 'types/locales';
import { createLanguageConfig } from '@tenants/utils/configs';

import type {
  HeadTagsConfig,
  HomeConfig,
  JoinExpConfig,
  Tenant,
  TenantConfigFilters,
  TenantConfigFooter,
  TenantConfigHeadTags,
  TenantConfigHeader,
  TenantConfigHome,
  TenantConfigJoinExp,
  FooterConfig,
  HeaderConfig,
  OrganizationConfig,
  TenantConfigOrganization,
  AddressPageConfig,
  TenantConfigAddressPage,
  TenantConfigObservability,
} from '@tenants/types';
import { CRITICAL_CITIES_US } from '@tenants/constants/critical-cities';

//#region Public Constants

export const EXP_REALTY_US_L18N_NAMESPACES = [
  Namespaces.AGENT_SEARCH,
  Namespaces.EXP_REALTY_US,
  Namespaces.JOIN_EXP_US,
  Namespaces.COMMON,
  Namespaces.SEARCH,
  Namespaces.FORM_ERROR,
  Namespaces.MODAL,
  Namespaces.INTERNAL_LINKS,
  Namespaces.HOME,
  Namespaces.LISTING,
  Namespaces.HOME_APPRAISAL,
  Namespaces.CONNECTION_REQUEST,
  Namespaces.CONNECT_WITH_AGENT,
] as const;

export const JOIN_EXP_US_GTM_CONTAINER_ID_KEY = 'JoinExpUs' as const;
//#endregion

//#region Public Types
export type ExpRealtyUSTenantConfig = TenantConfigHome & TenantConfigJoinExp & TenantConfigFilters & TenantConfigFooter & TenantConfigHeader & TenantConfigOrganization & TenantConfigHeadTags & TenantConfigObservability & TenantConfigAddressPage;
export type ExpRealtyUSTenant = Tenant<EXP_REALTY_US_TENANT_ID_TYPE, typeof ExprealtyUsTheme, ExpRealtyUSTenantConfig, typeof JOIN_EXP_US_GTM_CONTAINER_ID_KEY>;
//#endregion

//#region Config Factories
const header: HeaderConfig = createHeaderConfig();
const home: HomeConfig = createHomeConfig();
const joinExp: JoinExpConfig = createJoinExpConfig();
const footer: FooterConfig = createFooterConfig();
const organization: OrganizationConfig = createOrganizationConfig();
const headTags: HeadTagsConfig = createHeadTagsConfig();

const addressPage: AddressPageConfig = createAddressPageConfig();

// Supported countries for this tenant
const countries = [CountryCodeList.UNITED_STATES];
//#endregion

//#region Tenant Definition
export const expRealtyUSTenant: ExpRealtyUSTenant = {
  id: EXP_REALTY_US_TENANT_ID,
  hosts: configJSON.hosts[EXP_REALTY_US_TENANT_ID],
  contentHosts: configJSON.contentPageApi[EXP_REALTY_US_TENANT_ID] as ContentHosts,
  theme: ExprealtyUsTheme,
  localization: {
    countries,
    defaultCountry: CountryCodeList.UNITED_STATES,
    switchingEnabled: true,
    currencyMap: createCurrencyMap(countries),
    ...createLanguageConfig({
      globalLanguages: [SiteLanguage.English, SiteLanguage.Spanish],
      standardLanguages: [SiteLanguage.English, SiteLanguage.Spanish],
      globalDefaultLanguage: SiteLanguage.English,
      standardDefaultLanguage: SiteLanguage.English,
      namespaces: EXP_REALTY_US_L18N_NAMESPACES,
      allowGlobalLocaleSwitching: false,
    }),
  },
  config: {
    header,
    home,
    joinExp,
    filters: {
      hideExpiredListings: true, // US feeds don't have expired listings so remove the corresponding filter option
      countryCode: CountryCodeList.UNITED_STATES,
      showComingSoonFilter: true,
    },
    footer,
    organization,
    headTags,
    addressPage,
    observability: {
      enabled: true,
      areaPages: {
        enabled: true,
        criticalCities: CRITICAL_CITIES_US,
      },
    },
  },
  routes: {
    claimed: '*',
  },
  debug: {
    forceTenantHeaderValue: `useTenant="${EXP_REALTY_US_TENANT_ID}"`,
    forceTenantFeatureValue: EXP_REALTY_US_TENANT_ID,
  },
  features: {
    MyLinkMyLead: true,
    AgentSearch: true,
    AccessibilityWidget: true,
    TrustArc: {
      enabled: true,
      domain: 'exprealty.com',
    },
    ShowQuebecPopup: false,
    AiSearch: false,
    LoginRoleSelection: true,
    ShowHybridAreaMapPage: true,
    UseAreaPageV2: true,
    UseSmartSearch: true,
    UseNewAreaSearchDesign: true,
    UseLocalFocusHomepage: true,
    /** Building-grouped pins: off until rollout after Zoocasa; keep clusters + boundary like production. */
    UseBuildingGroupedPins: false,
    Experiments: {
      [AllowedExperimentKey.LISTING_HERO]: {
        enabled: true,
        observability: false,
      },
    },
  },
  googleTagManager: {
    containerIds: {
      [DEFAULT_GTM_CONTAINER_ID_KEY]: configJSON.googleTagManager.containerIdExpUs,
      [JOIN_EXP_US_GTM_CONTAINER_ID_KEY]: configJSON.googleTagManager.containerIdJoinExp,
    },
  },
  sso: {
    googleWebClientId: process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID_EXP || '',
    facebookAppId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID_EXP || '',
  },
  sitemap: {
    urls: [
      'https://www.exprealty.com/sitemaps/area-pages/sitemap-index.xml',
      'https://www.exprealty.com/sitemaps/address-pages/sitemap-index.xml',
      'https://www.exprealty.com/sitemaps/root-pages/sitemap-index.xml',
    ],
  },
  robots: {
    disallowDomains: [],
    disallowPaths: [],
  },
  pvr: {
    phoneNumber: '1-844-539-0722',
  },
  websiteUUID: configJSON.websiteUUID?.[EXP_REALTY_US_TENANT_ID],
  mobileAppBanner: null,
  consent: tcpaConsent(),
};